import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuth } from '@/auth/useAuth';
import { PRIVACY_URL, TERMS_URL } from '@/config/legal';
import { Button, Screen, TextField } from '../../ui';

export default function SignUp() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      /*
       * The email IS the username. The pool's UsernameAttributes is email/phone, so a
       * generated username throws InvalidParameterException — a regression this project
       * has already shipped once.
       */
      await signUp(email.trim(), password, { kind: 'email', acceptedTerms: accepted });
      navigate('/confirm', { state: { email: email.trim() } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create your account.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen title="Create account">
      <form onSubmit={submit} noValidate>
        <TextField label="Email" type="email" value={email} onChangeText={setEmail} placeholder="you@example.com" />
        <TextField label="Password" type="password" value={password} onChangeText={setPassword} />
        <label className="type-caption checkbox-row">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
          <span>
            I accept the <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms</a> and{' '}
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>
          </span>
        </label>
        {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
        <Button title="Create account" type="submit" loading={busy} disabled={!accepted} />
      </form>
      <p className="type-body auth-links">
        Already have an account? <Link to="/sign-in">Sign in</Link>
      </p>
    </Screen>
  );
}
